import { Box } from '@chakra-ui/react';
import { FileRejection, FileWithPath, useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';

import { filterValidFiles } from '../../utils/fileValidation';

type DropzoneProps = {
  onDrop: (a: File[]) => void;
  acceptedFiles?: File[];
  accept?: string | string[];
  multiple?: boolean;
  showFiles?: boolean;
  marginTop?: number;
  maxSize?: number;
};

const Dropzone = ({
  onDrop,
  acceptedFiles,
  accept,
  multiple,
  showFiles = true,
  marginTop = 0,
  maxSize,
}: DropzoneProps) => {
  const handleDropRejected = (fileRejections: FileRejection[]) => {
    const rejectedFiles = fileRejections.map(({ file }) => file);
    const passedTypeValidation = filterValidFiles(rejectedFiles);

    // For files that passed our validation but were rejected for other reasons (size, count),
    // show react-dropzone's original error messages.
    const validatedNames = new Set(passedTypeValidation.map((f) => f.name));
    fileRejections
      .filter(({ file }) => validatedNames.has(file.name))
      .forEach(({ file, errors }) => {
        const nonTypeErrors = errors.filter((e) => e.code !== 'file-invalid-type');
        if (nonTypeErrors.length > 0) {
          const reasons = nonTypeErrors.map((e) => e.message).join(', ');
          toast.error(`${file.name}: ${reasons}`);
        }
      });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
    maxSize,
    onDropRejected: handleDropRejected,
  });

  const files =
    showFiles && acceptedFiles?.length
      ? acceptedFiles.map((file: FileWithPath) => (
          <li
            key={file.path}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {file.path}
          </li>
        ))
      : null;

  return (
    <Box
      {...getRootProps()}
      border="dashed 2px"
      borderRadius="2xl"
      w="100%"
      minW={0}
      p="8"
      alignContent="center"
      justifyContent="center"
      borderColor="mediumElectricBlue"
      bg="gray.100"
      display="flex"
      overflow="hidden"
      marginTop={marginTop}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <p className="m-0">Drop the files here...</p>
      ) : files?.length ? (
        <ul className="m-0" style={{ maxWidth: '100%', overflow: 'hidden' }}>
          {files}
        </ul>
      ) : (
        <p className="m-0">Drag and Drop or Click to Select Files</p>
      )}
    </Box>
  );
};

export default Dropzone;
